import { Injectable } from '@angular/core';

export interface EncryptionData {
  key: CryptoKey;
  data: string;
}

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  async generateKey(): Promise<CryptoKey> {
    return window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  async importKey(keyString: string): Promise<CryptoKey> {
    const item = new Uint8Array(keyString.split(',').map((f) => +f));
    return window.crypto.subtle.importKey('raw', item, 'AES-GCM', true, ['encrypt', 'decrypt']);
  }

  async encryptData({ key, data }: EncryptionData): Promise<string> {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encodedText = new TextEncoder().encode(data);
    const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encodedText);
    const encryptedValue = btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
    const ivValue = btoa(String.fromCharCode(...iv));
    return `${encryptedValue}:${ivValue}`;
  }

  async decryptData({ key, data }: EncryptionData): Promise<string> {
    const [encryptedValue, ivValue] = data.split(':');
    const ciphertext = new Uint8Array(atob(encryptedValue).split('').map((char) => char.charCodeAt(0)));
    const iv = new Uint8Array(atob(ivValue).split('').map((char) => char.charCodeAt(0)));
    const decryptedResult = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return new TextDecoder().decode(decryptedResult);
  }
}
